import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { getModuleLabel } from '../shared/moduleMetadata';
import { getMasteryBand } from '../../shared/masteryBands';
import { formatRelativeTime, formatDateWithTime } from '../../../utils/formatters';
import { downloadCsv } from '../../../utils/csvExport';
import { TablePagination } from '../shared/TablePagination';
/**
 * Deterministic string-hash → small positive integer. Used as a stable
 * pseudo-time-offset for sessions that have no real `lastSeen` timestamp,
 * so the same session always renders at the same "time" instead of jumping
 * on every re-render (which the audit V12 / P2-1 flagged).
 */
function stableHash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = h * 31 + input.charCodeAt(i) | 0;
  }
  return h >>> 0;
}
import { Activity, Filter, Search, Clock, BookOpen, TrendingUp, Download } from 'lucide-react';
interface SessionAnswer {
  isCorrect: boolean;
}
interface SessionLog {
  id: string;
  accountId: string;
  subjectId: string;
  startTime: number;
  answers: SessionAnswer[];
}
export function LogsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  // `now` captured once per render so deterministic fallback timestamps
  // remain stable within a single render pass.
  const now = Date.now();
  const sessions = useMemo(() => {
    const allSessions: SessionLog[] = [];
    allAccounts.filter(a => a.role === 'learner').forEach(learner => {
      const data = getLearnerData(learner.id);
      if (!data?.familyMastery) return;
      Object.values(data.familyMastery).forEach(mastery => {
        if (mastery.attempts > 0) {
          const subjectId = mastery.familyId.split('-')[0];
          // Deterministic offset within last 7 days when no real lastSeen.
          // Same (learner, family) pair → same pseudo-time on every render.
          const fallbackOffset = stableHash(`${learner.id}:${mastery.familyId}`) % (7 * 86400000);
          allSessions.push({
            id: `sess-${learner.id}-${mastery.familyId}`,
            accountId: learner.id,
            subjectId: subjectId || 'UNK',
            startTime: mastery.lastSeen ?? now - fallbackOffset,
            answers: Array.from({
              length: Math.min(10, mastery.attempts)
            }).map((_, i) => ({
              isCorrect: i < mastery.correct / mastery.attempts * Math.min(10, mastery.attempts)
            }))
          });
        }
      });
    });
    return allSessions.sort((a, b) => b.startTime - a.startTime);
  }, [allAccounts, getLearnerData, now]);
  const [search, setSearch] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '1d' | '7d' | '30d'>('7d');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const dateThreshold = useMemo(() => {
    const now = Date.now();
    if (dateFilter === '1d') return now - 86400000;
    if (dateFilter === '7d') return now - 7 * 86400000;
    if (dateFilter === '30d') return now - 30 * 86400000;
    return 0;
  }, [dateFilter]);
  const allSubjects = useMemo(() => Array.from(new Set(sessions.map(s => s.subjectId).filter(Boolean))).sort(), [sessions]);
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      const matchDate = s.startTime >= dateThreshold;
      const matchSubject = !subjectFilter || s.subjectId === subjectFilter;
      const account = allAccounts.find(a => a.id === s.accountId);
      const matchSearch = !search || (account?.displayName ?? '').toLowerCase().includes(search.toLowerCase()) || s.subjectId?.toLowerCase().includes(search.toLowerCase()) || s.id?.toLowerCase().includes(search.toLowerCase());
      return matchDate && matchSubject && matchSearch;
    }).sort((a, b) => b.startTime - a.startTime);
  }, [sessions, allAccounts, dateThreshold, subjectFilter, search]);
  const paginatedSessions = useMemo(() => filteredSessions.slice(page * pageSize, (page + 1) * pageSize), [filteredSessions, page, pageSize]);
  const stats = useMemo(() => {
    const now = Date.now();
    const today = sessions.filter(s => s.startTime >= now - 86400000);
    const week = sessions.filter(s => s.startTime >= now - 7 * 86400000);
    const avgScore = filteredSessions.length > 0 ? Math.round(filteredSessions.reduce((sum, s) => {
      const correct = s.answers?.filter(a => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      return sum + (total > 0 ? correct / total * 100 : 0);
    }, 0) / filteredSessions.length) : 0;
    return {
      today: today.length,
      week: week.length,
      total: sessions.length,
      avgScore
    };
  }, [sessions, filteredSessions]);
  const exportCsv = () => {
    const rows = [['ID', 'Leerling', 'Vak', 'Score %', 'Vragen', 'Datum'], ...filteredSessions.map(s => {
      const account = allAccounts.find(a => a.id === s.accountId);
      const correct = s.answers?.filter(a => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      const pct = total > 0 ? Math.round(correct / total * 100) : 0;
      return [s.id, account?.displayName ?? '?', s.subjectId, pct, total, formatDateWithTime(s.startTime)];
    })];
    const csv = rows.map(r => r.join(',')).join('\n');
    downloadCsv(csv, 'sessies');
  };
  return <PageLayout title="Activiteitenlog" subtitle="Overzicht van alle oefensessies" actions={<div className="flex items-center gap-2" data-id="element-7054" data-mp="24lhy9d9tgr9k">
          {/*
                  Sessions are derived from real `familyMastery` aggregates but the
                  per-answer breakdown and fallback timestamps are reconstructed
                  client-side. Surface that honestly to admins (audit §19.2.8 / V15).
                */}
          <ConnectionBadge status="mock" label="Demo-sessies" data-id="element-7055" data-mp="1ztqfvq0tdrui" />
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white shadow-sm" data-id="element-7056" data-mp="2nkm287mi3uud">
            <Download className="w-4 h-4" data-id="element-7057" data-mp="gv29wu40h3av" /> Export CSV
          </button>
        </div>} breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Activiteitenlog'
  }]} data-id="element-7053" data-mp="1gw72693evl1r">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-7058" data-mp="lmtpyapjjqmr">
        <StatCard label="Vandaag" value={stats.today} icon={<Clock className="w-5 h-5" data-id="element-7060" data-mp="3u59culv11bkg" />} color="blue" data-id="element-7059" data-mp="lppqm6srwtew" />
        <StatCard label="Deze week" value={stats.week} icon={<Activity className="w-5 h-5" data-id="element-7062" data-mp="6ypf1swqcjej" />} color="emerald" data-id="element-7061" data-mp="3p5djdgjyh2j5" />
        <StatCard label="Totaal sessies" value={stats.total} icon={<BookOpen className="w-5 h-5" data-id="element-7064" data-mp="1idm5n78kbgo0" />} color="purple" data-id="element-7063" data-mp="1zm9m7cu8b61" />
        <StatCard label="Gem. score" value={`${stats.avgScore}%`} icon={<TrendingUp className="w-5 h-5" data-id="element-7066" data-mp="1de9nv0wjszsa" />} color="amber" data-id="element-7065" data-mp="1ifp84qqwj29m" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-7067" data-mp="1xet16mtxprcp">
        <div className="flex-1 w-full sm:max-w-sm" data-id="element-7068" data-mp="26hkeytaa8z7i">
          <Input type="text" placeholder="Zoek op leerling of vak..." value={search} onChange={e => {
          setSearch(e.target.value);
          setPage(0);
        }} icon={<Search className="w-4 h-4" data-id="element-7070" data-mp="2l5ygn0vyqhmh" />} size="sm" data-id="element-7069" data-mp="26g0yp2ommo5q" />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1 sm:pb-0" data-id="element-7071" data-mp="2q5bdmegjt52s">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0" data-id="element-7072" data-mp="2q3gosp25ostf">
            {(['all', '1d', '7d', '30d'] as const).map(d => <button key={d} onClick={() => {
            setDateFilter(d);
            setPage(0);
          }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${dateFilter === d ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7073" data-mp="vslglnuvvfr6">
                {d === 'all' ? 'Alles' : d === '1d' ? '24u' : d === '7d' ? '7d' : '30d'}
              </button>)}
          </div>

          <div className="flex-shrink-0" data-id="element-7074" data-mp="1b64d4bz69dob">
            <Select value={subjectFilter} onChange={e => {
            setSubjectFilter(e.target.value);
            setPage(0);
          }} options={allSubjects.map(s => ({
            value: s,
            label: getModuleLabel(s) || s
          }))} placeholder="Alle vakken" icon={<Filter className="w-3.5 h-3.5" data-id="element-7076" data-mp="3hyoew51qv0c9" />} fullWidth={false} size="sm" data-id="element-7075" data-mp="168vvcchxrbnl" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-7077" data-mp="jkr4an09hm63">
        <div className="overflow-x-auto" data-id="element-7078" data-mp="eplido6v07i0">
          <table className="w-full text-sm text-left" data-id="element-7079" data-mp="sex72yskztej">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-medium" data-id="element-7080" data-mp="sgglejuofudo">
              <tr data-id="element-7081" data-mp="nn5md4w3whwb">
                <th className="px-4 py-3" data-id="element-7082" data-mp="nmm4fo6ghtia">Sessie ID</th>
                <th className="px-4 py-3" data-id="element-7083" data-mp="12c1sgwelrkua">Leerling</th>
                <th className="px-4 py-3" data-id="element-7084" data-mp="xe8n25l4iuv2">Vak</th>
                <th className="px-4 py-3" data-id="element-7085" data-mp="xdgrgurj0x4s">Score</th>
                <th className="px-4 py-3" data-id="element-7086" data-mp="1bdrj400nufr0">Tijdstip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50" data-id="element-7087" data-mp="17cg22ivur2uf">
              {paginatedSessions.length === 0 ? <tr data-id="element-7088" data-mp="3d54m2jj2idkf">
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400" data-id="element-7089" data-mp="3d4mjl55tb342">
                    Geen sessies gevonden
                  </td>
                </tr> : paginatedSessions.map(session => {
              const account = allAccounts.find(a => a.id === session.accountId);
              const correct = session.answers?.filter(a => a.isCorrect).length ?? 0;
              const total = session.answers?.length ?? 0;
              const pct = total > 0 ? Math.round(correct / total * 100) : 0;
              return <tr key={session.id} className="hover:bg-gray-50 transition-colors" data-id="element-7090" data-mp="2kcj5yso53tmk">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500" data-id="element-7091" data-mp="2b9rvow7033wi">
                        {session.id.split('-').slice(0, 2).join('-')}...
                      </td>
                      <td className="px-4 py-3" data-id="element-7092" data-mp="9iwtnmm7a8hc">
                        <div className="flex items-center gap-2" data-id="element-7093" data-mp="9hlpvmsr90q8">
                          <Avatar name={account?.displayName ?? '?'} data-id="element-7094" data-mp="74rs1vrjyalh" />
                          <span className="font-medium text-gray-900" data-id="element-7095" data-mp="71x8e6gd4tt4">
                            {account?.displayName ?? 'Onbekend'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-7096" data-mp="3paehf3un5n33">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700" data-id="element-7097" data-mp="3pbpjmz8fums3">
                          {getModuleLabel(session.subjectId) || session.subjectId}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-7098" data-mp="h0ldy5r5vgfx">
                        <div className="flex items-center gap-2" data-id="element-7099" data-mp="c3ut754evwo6">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-7100" data-mp="c1ier1xv44fv">
                            <div className={`h-full rounded-full ${getMasteryBand(pct).barClass}`} style={{
                        width: `${pct}%`
                      }} data-id="element-7101" data-mp="19kjwl390f6zo" />
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-8" data-id="element-7102" data-mp="1m1cj1t2h9cf3">
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap" data-id="element-7103" data-mp="1v2amoa907hx5">
                        <div className="flex flex-col" data-id="element-7104" data-mp="1v06jxvmucoyg">
                          <span data-id="element-7105" data-mp="1zzsyq9ibid9d">{formatDateWithTime(session.startTime)}</span>
                          <span className="text-xs text-gray-400" data-id="element-7106" data-mp="24rkhvyz59gph">
                            {formatRelativeTime(session.startTime, true)}
                          </span>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <TablePagination page={page} pageSize={pageSize} total={filteredSessions.length} onPageChange={setPage} onPageSizeChange={size => {
        setPageSize(size);
        setPage(0);
      }} data-id="element-7107" data-mp="3gg8at92hlfj1" />
      </div>
    </PageLayout>;
}